import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';

export default function BudgetArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Superbuy Spreadsheet Pricing & Budget Guide: Track Every Dollar Across Borders',
    description: 'Learn how to use a superbuy spreadsheet to manage cross-border shopping budgets, track multi-currency pricing, compare total landed costs, and never overspend on a haul again.',
    author: { '@type': 'Organization', name: 'Superbuy Team' },
    datePublished: '2026-07-13',
    dateModified: '2026-07-13',
    publisher: { '@type': 'Organization', name: 'Superbuy Spreadsheet', url: 'https://superbuyspreadsheet.buzz' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://superbuyspreadsheet.buzz/article/superbuy-spreadsheet-pricing-budget-guide' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'How do I handle currency conversion in my superbuy spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Use Google Sheets GOOGLEFINANCE function to pull live exchange rates. For CNY to USD, the formula is =GOOGLEFINANCE(\"CURRENCY:CNYUSD\"). Multiply your CNY price by this rate to get real-time USD equivalents. Add a buffer of 2-3% to account for payment processor fees.' } },
      { '@type': 'Question', name: 'What hidden costs should I include in my superbuy budget?', acceptedAnswer: { '@type': 'Answer', text: 'Beyond the item price, your budget should include domestic Chinese shipping (usually 10-15 CNY per item), agent service fees (typically 3-10% of item cost), international freight (varies by weight and shipping line), and potential customs duties based on your country threshold.' } },
      { '@type': 'Question', name: 'How can I automatically calculate total haul cost in my sheet?', acceptedAnswer: { '@type': 'Answer', text: 'Create a summary section with SUM formulas for all item costs, domestic shipping, and fees. Add estimated international shipping based on total weight. Use conditional formatting to highlight when your running total exceeds your budget threshold so you can adjust before submitting the order.' } },
      { '@type': 'Question', name: 'Is it cheaper to ship items together or separately?', acceptedAnswer: { '@type': 'Answer', text: 'Generally, consolidating items into one parcel is more cost-effective since the first kilogram costs the most and each additional kilogram is cheaper. Use a shipping cost estimator column in your superbuy sheet to compare consolidated versus split shipping totals.' } },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://superbuyspreadsheet.buzz/' },
      { '@type': 'ListItem', position: 2, name: 'Budget Guide', item: 'https://superbuyspreadsheet.buzz/article/superbuy-spreadsheet-pricing-budget-guide' },
    ],
  };

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-heading font-bold text-foreground-950">Superbuy</span>
            <span className="text-lg font-heading font-light text-foreground-500">Spreadsheet</span>
          </Link>
          <Link to="/" className="whitespace-nowrap text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 inline-flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back to Home
          </Link>
        </div>
      </nav>

      <header className="bg-background-50 pt-12 pb-8 md:pt-20 md:pb-12">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full uppercase tracking-wide">Budget Guide</span>
            <span className="text-xs text-foreground-400">13 min read</span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-[1.08] mb-5">
            Superbuy Spreadsheet Pricing & Budget Guide: Track Every Dollar Across Borders
          </h1>
          <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-8 max-w-2xl">
            Cross-border shopping involves multiple currencies, hidden fees, and shipping costs that can surprise even experienced buyers. This guide shows you exactly how to build a pricing and budget tracking system into your superbuy spreadsheet — so you always know the true cost of every haul.
          </p>
          <div className="flex items-center gap-4 pb-8 border-b border-background-200">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
              <span className="text-accent-600 font-bold text-sm">SS</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800">Superbuy Team</p>
              <p className="text-xs text-foreground-400">Published July 13, 2026 &middot; Last updated July 13, 2026</p>
            </div>
          </div>
        </div>
      </header>

      <article className="bg-background-50 pb-20">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">

            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">Quick Answer</h2>
              <p className="text-sm text-foreground-600">
                A <strong>superbuy spreadsheet budget system</strong> tracks every cost layer in cross-border shopping — item price in CNY, domestic shipping, agent fees, currency conversion, international freight, and customs duties — and automatically calculates your total landed cost per item and per haul. With live exchange rate formulas and budget alerts, you will always know exactly how much you are spending before money leaves your account.
              </p>
            </div>

            <nav className="bg-background-100 rounded-2xl p-6 md:p-8" aria-label="Table of Contents">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-4">Table of Contents</h2>
              <ol className="space-y-2 text-sm">
                <li><a href="#cost-layers" className="text-foreground-600 hover:text-foreground-950 transition-colors">1. Understanding All the Cost Layers</a></li>
                <li><a href="#currency-conversion" className="text-foreground-600 hover:text-foreground-950 transition-colors">2. Currency Conversion with Live Rates</a></li>
                <li><a href="#shipping-math" className="text-foreground-600 hover:text-foreground-950 transition-colors">3. Shipping Cost Math — Domestic & International</a></li>
                <li><a href="#budget-alerts" className="text-foreground-600 hover:text-foreground-950 transition-colors">4. Setting Up Budget Alerts & Thresholds</a></li>
                <li><a href="#summary-dashboard" className="text-foreground-600 hover:text-foreground-950 transition-colors">5. Building a Haul Summary Dashboard</a></li>
                <li><a href="#cost-saving" className="text-foreground-600 hover:text-foreground-950 transition-colors">6. Cost-Saving Strategies & Tips</a></li>
                <li><a href="#faq" className="text-foreground-600 hover:text-foreground-950 transition-colors">7. Frequently Asked Questions</a></li>
              </ol>
            </nav>

            <section id="cost-layers">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">Understanding All the Cost Layers</h2>
              <p className="text-sm md:text-base mb-4">
                The biggest mistake new cross-border shoppers make is looking only at the item price. In reality, every product you buy has at least five cost layers stacked on top of each other. A superbuy spreadsheet that accounts for all of them gives you the true picture.
              </p>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-6">
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">The Complete Cost Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-background-200">
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">1. Item Price (CNY)</p>
                      <p className="text-xs text-foreground-500">The listed price on Weidian, Taobao, or 1688</p>
                    </div>
                    <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">Base Cost</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-background-200">
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">2. Domestic Shipping</p>
                      <p className="text-xs text-foreground-500">Shipping from the seller to your agent warehouse in China</p>
                    </div>
                    <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">~10-15 CNY</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-background-200">
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">3. Agent Service Fee</p>
                      <p className="text-xs text-foreground-500">Percentage fee charged by your buying agent per item</p>
                    </div>
                    <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">3-10%</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-background-200">
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">4. International Freight</p>
                      <p className="text-xs text-foreground-500">Shipping from China to your country based on weight</p>
                    </div>
                    <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">Varies by weight</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">5. Customs & Duties</p>
                      <p className="text-xs text-foreground-500">Import taxes based on your country thresholds</p>
                    </div>
                    <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">Country-dependent</span>
                  </div>
                </div>
              </div>
            </section>

            <section id="currency-conversion">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">Currency Conversion with Live Rates</h2>
              <p className="text-sm md:text-base mb-4">
                Most Chinese marketplaces list prices in CNY, but you likely think in USD, EUR, or GBP. Manually converting every price with a calculator is tedious and error-prone. The solution is embedding live exchange rate formulas directly into your superbuy spreadsheet.
              </p>
              <p className="text-sm md:text-base mb-4">
                If you are using Google Sheets, the <code className="bg-background-100 px-2 py-0.5 rounded text-xs">GOOGLEFINANCE</code> function pulls real-time currency rates. A simple formula like <code className="bg-background-100 px-2 py-0.5 rounded text-xs">=GOOGLEFINANCE(&quot;CURRENCY:CNYUSD&quot;)</code> gives you the current CNY to USD rate. Multiply your item price by this rate and you instantly see the USD equivalent. Add a 2-3% buffer to account for payment processor exchange margins — your actual cost will always be slightly higher than the mid-market rate.
              </p>

              <div className="bg-primary-50 border border-primary-200 rounded-2xl p-6 my-6">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-primary-500 text-xl mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Pro Tip: Rate History</h4>
                    <p className="text-xs text-foreground-600">
                      CNY to USD rates fluctuate. If you are planning a large haul, track the rate over several weeks in a separate sheet. When the rate is favorable, pull the trigger on your purchases to maximize your buying power.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section id="shipping-math">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">Shipping Cost Math — Domestic & International</h2>
              <p className="text-sm md:text-base mb-4">
                Shipping costs can easily represent 30-50% of your total haul cost, especially for heavier items like sneakers with boxes or thick winter jackets. A superbuy spreadsheet that accurately estimates shipping before you commit is essential for staying within budget.
              </p>
              <p className="text-sm md:text-base mb-4">
                For domestic shipping within China, most sellers charge a flat 10-15 CNY per order. This is relatively predictable. The real variable is international freight, which is almost always calculated by weight. Add a column in your sheet for estimated weight per item. Then, in a summary section, total all item weights and multiply by your agent&apos;s published shipping rate per kilogram for your preferred shipping line.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-plane-line text-primary-600 text-sm" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Express Lines</h3>
                  <p className="text-xs text-foreground-500">7-15 days delivery. Higher cost per kg but fastest and most reliable. Good for urgent hauls.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                    <i className="ri-ship-line text-accent-600 text-sm" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Economy Lines</h3>
                  <p className="text-xs text-foreground-500">15-45 days delivery. Lower cost per kg. Best for budget-conscious hauls where speed is not critical.</p>
                </div>
              </div>
            </section>

            <section id="budget-alerts">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">Setting Up Budget Alerts & Thresholds</h2>
              <p className="text-sm md:text-base mb-4">
                It is easy to get carried away adding items to your superbuy sheet — especially when prices look cheap in CNY. Budget alerts keep you grounded by showing exactly when your running total approaches your preset spending limit.
              </p>
              <p className="text-sm md:text-base mb-4">
                Create a cell at the top of your sheet for your haul budget in your home currency. Below it, add a running total formula that sums all item total costs including domestic shipping and estimated international freight. Next to that, add a percentage-used formula that divides the running total by your budget. Use conditional formatting to turn this cell yellow when you reach 70% and red when you exceed 90%. This visual cue is impossible to ignore and prevents the all-too-common &ldquo;oops, I spent double what I planned&rdquo; moment.
              </p>
            </section>

            <section id="summary-dashboard">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">Building a Haul Summary Dashboard</h2>
              <p className="text-sm md:text-base mb-4">
                A dedicated summary section at the top of your superbuy spreadsheet gives you an instant financial overview. Here is what to include:
              </p>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-6">
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">Dashboard Metrics</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-background-200">
                    <span className="text-sm text-foreground-600">Total Items</span>
                    <span className="text-sm font-semibold text-foreground-800">=COUNTA formula</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-background-200">
                    <span className="text-sm text-foreground-600">Subtotal (CNY)</span>
                    <span className="text-sm font-semibold text-foreground-800">=SUM of item prices</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-background-200">
                    <span className="text-sm text-foreground-600">Subtotal (USD)</span>
                    <span className="text-sm font-semibold text-foreground-800">=CNY subtotal × live rate</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-background-200">
                    <span className="text-sm text-foreground-600">Total Weight</span>
                    <span className="text-sm font-semibold text-foreground-800">=SUM of item weights</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-background-200">
                    <span className="text-sm text-foreground-600">Est. Shipping</span>
                    <span className="text-sm font-semibold text-foreground-800">=Total weight × rate per kg</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-semibold text-foreground-900">Grand Total (USD)</span>
                    <span className="text-base font-bold text-foreground-950">All costs combined</span>
                  </div>
                </div>
              </div>
            </section>

            <section id="cost-saving">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">Cost-Saving Strategies & Tips</h2>
              <div className="space-y-4">
                <div className="flex gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-green-100">
                    <i className="ri-stack-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Consolidate shipments</h4>
                    <p className="text-xs text-foreground-500">Wait until you have 5-8 items at the warehouse, then ship them together. The combined weight often costs less per kilogram than shipping individually.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-green-100">
                    <i className="ri-box-3-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Remove shoe boxes</h4>
                    <p className="text-xs text-foreground-500">Sneaker boxes add 300-500g per pair. Request your agent to remove boxes before international shipping to cut freight costs by 20-30% on sneaker-heavy hauls.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-green-100">
                    <i className="ri-calendar-check-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Time your purchases</h4>
                    <p className="text-xs text-foreground-500">Avoid shopping during Chinese holidays like Singles Day (11.11) and Chinese New Year. Shipping costs spike and delivery times stretch significantly during these periods.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="faq">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">How do I handle currency conversion in my superbuy spreadsheet?</h3>
                  <p className="text-xs text-foreground-500">Use Google Sheets GOOGLEFINANCE function to pull live exchange rates. Multiply your CNY price by this rate to get real-time USD equivalents. Add a buffer of 2-3% to account for payment processor fees.</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">What hidden costs should I include in my budget?</h3>
                  <p className="text-xs text-foreground-500">Beyond the item price, include domestic Chinese shipping, agent service fees, international freight, and potential customs duties based on your country threshold.</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">How can I automatically calculate total haul cost?</h3>
                  <p className="text-xs text-foreground-500">Create a summary section with SUM formulas for all costs. Add estimated international shipping based on total weight. Use conditional formatting to highlight when your running total exceeds your budget.</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">Is it cheaper to ship items together or separately?</h3>
                  <p className="text-xs text-foreground-500">Generally, consolidating items into one parcel is more cost-effective since each additional kilogram is cheaper. Use a shipping cost estimator column in your sheet to compare both options.</p>
                </div>
              </div>
            </section>

            <div className="pt-8 border-t border-background-200">
              <div className="bg-foreground-950 rounded-3xl p-8 md:p-10 text-center">
                <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">Take Control of Your Shopping Budget</h3>
                <p className="text-sm text-white/50 mb-6 max-w-md mx-auto">
                  Browse our complete product catalog and start building a cost-tracked haul today. Every purchase documented, every dollar accounted for.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link to="/" className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3 bg-white text-foreground-950 font-semibold text-sm rounded-full hover:bg-white/90 transition-colors duration-200">
                    <i className="ri-home-4-line" /> Back to Homepage
                  </Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3 border border-white/30 text-white font-medium text-sm rounded-full hover:bg-white/10 transition-colors duration-200">
                    Start Shopping <i className="ri-external-link-line" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">Continue Reading</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/article/superbuy-spreadsheet-ultimate-guide" className="block p-4 bg-white rounded-xl hover:bg-background-50 transition-colors duration-200">
                  <p className="text-sm font-medium text-foreground-800 mb-1">The Complete Superbuy Spreadsheet Guide</p>
                  <p className="text-xs text-foreground-400">Everything from setup to advanced automation</p>
                </Link>
                <Link to="/article/track-qc-photos-superbuy-sheet" className="block p-4 bg-white rounded-xl hover:bg-background-50 transition-colors duration-200">
                  <p className="text-sm font-medium text-foreground-800 mb-1">QC Photos Tracking Guide</p>
                  <p className="text-xs text-foreground-400">Master your quality control inspection workflow</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}